import { logger } from './logger';
import { isEvent, isRPCErrorReply, isRPCReply } from './typeGuards';
import { generateId } from './utils';
export class Channel {
    /**
     * RPC communication based on window postMessage
     * @param target - essentially a window obj we want to send messages to
     * @param chanId - Channel id. Allows one frame to contain multiple plugins.
     * @param options.sendOnly - When true, skip attaching a message listener.
     */
    constructor(target, chanId, { sendOnly = false } = {}) {
        this.target = target;
        this.chanId = chanId;
        this.pendingCalls = new Map();
        this.eventListeners = new Set();
        this.onMessage = (evt) => {
            if (evt.data.chanId !== this.chanId) {
                return;
            }
            const data = evt.data;
            logger.debug({ evt }, `Message received for channel ${this.chanId}`);
            if (isRPCErrorReply(data)) {
                const [, reject] = this.pendingCalls.get(data.replyTo) ?? [];
                if (!reject) {
                    logger.debug({ evt }, 'Reject fn doesnt exist');
                    return;
                }
                this.pendingCalls.delete(data.replyTo);
                return reject(new Error(data.error));
            }
            if (isRPCReply(data)) {
                const [resolve] = this.pendingCalls.get(data.replyTo) ?? [];
                if (!resolve) {
                    logger.debug({ evt }, 'Resolve fn doesnt exist');
                    return;
                }
                this.pendingCalls.delete(data.replyTo);
                return resolve(data.payload);
            }
            if (isEvent(data)) {
                for (const listener of this.eventListeners) {
                    void listener(data);
                }
            }
        };
        if (!sendOnly) {
            globalThis.addEventListener('message', this.onMessage);
        }
    }
    addEventListener(listener) {
        this.eventListeners.add(listener);
    }
    removeEventListener(listener) {
        this.eventListeners.delete(listener);
    }
    // TODO: Should app->plugin events have to be explicitly registered for? We
    // likely already need some routing for e.g. plugin button interactions,
    // which should probably only go to the plugin it's from
    emitEvent(event) {
        this.target.postMessage({ chanId: this.chanId, ...event }, '*');
    }
    callRPC(method, payload, transfer) {
        const id = generateId();
        logger.debug({ payload, id }, `'${method}' called for channel ${this.chanId}`);
        return new Promise((resolve, reject) => {
            this.pendingCalls.set(id, [resolve, reject]);
            this.target.postMessage({
                rpc: method,
                payload,
                id,
                chanId: this.chanId,
            }, '*', transfer);
        });
    }
    replyRPC(event) {
        this.emitEvent(event);
    }
    replyRPCError(event) {
        this.emitEvent(event);
    }
    sendEvent(event) {
        this.emitEvent(event);
    }
    unregister() {
        globalThis.removeEventListener('message', this.onMessage);
    }
    get targetWindow() {
        return this.target;
    }
}
//# sourceMappingURL=channel.js.map